<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import CrmDealsAPI from 'dashboard/api/saas/crmDeals';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

// Motivos aceitos pelo backend (Saas::CrmDeal::LOST_REASONS). Ficam aqui em vez
// de vir por requisicao porque sao constantes no modelo.
const LOST_REASONS = [
  'price',
  'competitor',
  'credit_denied',
  'gave_up',
  'went_silent',
  'just_browsing',
  'other',
];

const isLoading = ref(false);
const isSaving = ref(false);
const pendingStageId = ref(null);
const lostReason = ref('');
const lostReasonNote = ref('');

// O card DESTA conversa, buscado direto do servidor.
//
// Antes este painel procurava o card dentro da lista do quadro
// (store crmDeals), e isso estava errado por dois motivos somados:
//
//   1. Comparava `deal.conversation.id` com o `conversationId` que chega do
//      ContactPanel. Mas o `id` de conversa que o front usa e o **display_id**
//      (o serializer faz `json.id conversation.display_id`), enquanto o card
//      devolve o id interno. Nesta conta os dois nunca coincidem: sao 655
//      conversas e em nenhuma `id == display_id` (o deslocamento e 10). Ou
//      seja, a busca falhava para todas, e o bloco "Etapa do funil" aparecia
//      vazio para qualquer conversa.
//   2. Mesmo com a comparacao certa, a lista do quadro corta em 200 cards e o
//      funil ja passa de 640. A maioria dos cards nem chegava ao navegador.
//
// Buscar pelo `conversation_display_id` resolve os dois de uma vez, e de
// quebra nao sobrescreve a lista do quadro (o `crmDeals/get` faz SET_CRM_DEALS,
// que troca os registros inteiros).
const deal = ref(null);

const stages = computed(() => store.getters['pipelineStages/getStages']);

const currentStage = computed(() =>
  stages.value.find(stage => stage.id === deal.value?.saas_pipeline_stage_id)
);

// O estagio escolhido no select exige motivo? So os de tipo "lost".
const pendingStage = computed(() =>
  stages.value.find(stage => stage.id === pendingStageId.value)
);

const needsLostReason = computed(
  () => pendingStage.value?.stage_type === 'lost'
);

const resetPending = () => {
  pendingStageId.value = null;
  lostReason.value = '';
  lostReasonNote.value = '';
};

const carregar = async () => {
  if (isLoading.value) return;
  isLoading.value = true;
  try {
    let pipelines = store.getters['pipelines/getPipelines'];
    if (!pipelines || pipelines.length === 0) {
      await store.dispatch('pipelines/get');
      pipelines = store.getters['pipelines/getPipelines'];
    }
    const pipeline = pipelines?.[0];
    if (!pipeline) return;

    if (!stages.value.length) {
      await store.dispatch('pipelineStages/get', pipeline.id);
    }

    const { data } = await CrmDealsAPI.get({
      pipeline_id: pipeline.id,
      conversation_display_id: props.conversationId,
    });
    deal.value = (data?.payload || [])[0] || null;
  } catch (error) {
    // silencioso: o painel simplesmente nao aparece se o CRM nao responder
    deal.value = null;
  } finally {
    isLoading.value = false;
  }
};

onMounted(carregar);

// Ao trocar de conversa, limpa o formulario pela metade e busca o card da nova
watch(
  () => props.conversationId,
  () => {
    resetPending();
    deal.value = null;
    carregar();
  }
);

const moveDeal = async () => {
  if (!deal.value || !pendingStageId.value) return;
  if (needsLostReason.value && !lostReason.value) return;

  isSaving.value = true;
  try {
    const payload = {
      id: deal.value.id,
      saas_pipeline_stage_id: pendingStageId.value,
    };
    if (lostReason.value) payload.lost_reason = lostReason.value;
    if (lostReasonNote.value) payload.lost_reason_note = lostReasonNote.value;

    // Continua passando pelo store para o quadro do CRM, se estiver aberto em
    // outra aba do mesmo app, receber o card atualizado.
    const atualizado = await store.dispatch('crmDeals/move', payload);
    if (atualizado) deal.value = atualizado;
    useAlert(t('CRM.CONVERSATION_STAGE.MOVED'));
    resetPending();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        error?.message ||
        t('CRM.CONVERSATION_STAGE.MOVE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};

const onStageChange = event => {
  const value = Number(event.target.value);
  pendingStageId.value = value || null;
  lostReason.value = '';
  lostReasonNote.value = '';
  // etapa que nao precisa de motivo move na hora, sem passo extra
  if (value && !needsLostReason.value) moveDeal();
};
</script>

<template>
  <div v-if="deal && stages.length" class="flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <span
        v-if="currentStage"
        class="w-2 h-2 rounded-full flex-shrink-0"
        :style="{ backgroundColor: currentStage.color || '#94a3b8' }"
      />
      <span class="text-sm text-slate-700 dark:text-slate-200">
        {{ currentStage?.name || $t('CRM.CONVERSATION_STAGE.NO_STAGE') }}
      </span>
    </div>

    <select
      class="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-md py-1.5 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
      :disabled="isSaving"
      :value="pendingStageId || deal.saas_pipeline_stage_id"
      @change="onStageChange"
    >
      <option v-for="stage in stages" :key="stage.id" :value="stage.id">
        {{ stage.name }}
      </option>
    </select>

    <div v-if="needsLostReason" class="flex flex-col gap-2">
      <select
        v-model="lostReason"
        class="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-md py-1.5 px-2 text-sm"
      >
        <option value="">
          {{ $t('CRM.CONVERSATION_STAGE.PICK_LOST_REASON') }}
        </option>
        <option v-for="reason in LOST_REASONS" :key="reason" :value="reason">
          {{ $t(`CRM.LOST_REASONS.${reason}`) }}
        </option>
      </select>

      <input
        v-model="lostReasonNote"
        class="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-md py-1.5 px-2 text-sm"
        :placeholder="$t('CRM.CONVERSATION_STAGE.LOST_NOTE_PLACEHOLDER')"
      />

      <div class="flex items-center gap-2">
        <button
          type="button"
          class="bg-n-brand hover:brightness-110 text-slate-950 px-3 py-1.5 text-xs rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!lostReason || isSaving"
          @click="moveDeal"
        >
          {{ $t('CRM.CONVERSATION_STAGE.CONFIRM_LOST') }}
        </button>
        <button
          type="button"
          class="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 cursor-pointer"
          @click="resetPending"
        >
          {{ $t('CRM.CONVERSATION_STAGE.CANCEL') }}
        </button>
      </div>
    </div>
  </div>
</template>
